import { SplitBox } from "@dropbox/dash-component-library";
import { Button } from "@dropbox/dig-components/buttons";
import { Text, Title } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  AiChatLine,
  EditPictogram,
  SearchPictogram,
} from "@dropbox/dig-icons/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Dismiss_BridgeWelcomeModal } from "@mirage/analytics/events/types/dismiss_bridge_welcome_modal";
import { PAP_Shown_BridgeWelcomeModal } from "@mirage/analytics/events/types/shown_bridge_welcome_modal";
import { useFinishOnboarding } from "@mirage/growth/onboarding/flows/utils";
import { InfoModal } from "@mirage/mosaics/Modals";
import { useDoOnceOnMount } from "@mirage/shared/hooks/useDoOnceOnMount";
import i18n from "@mirage/translations";
import type { ComponentType, SVGAttributes } from "react";
import * as React from "react";
import styles from "./WelcomeModal.module.css";

const HERO_VIDEO =
  "https://assets.dropbox.com/images/dashweb/onboarding/holistic-ai-ui.webm";

type Props = {
  open: boolean;
  onClose?: (isAddApps: boolean) => void;
  variant?: "default" | "addApps" | "override";
};

const FEATURES: [ComponentType<SVGAttributes<SVGElement>>, string][] = [
  [SearchPictogram, i18n.t("welcome_search_blurb")],
  [AiChatLine, i18n.t("welcome_ask_blurb")],
  [EditPictogram, i18n.t("welcome_write_blurb")],
];

export default function WelcomeModal({
  open,
  onClose,
  variant = "default",
}: Props) {
  const { reportPapEvent } = useMirageAnalyticsContext();
  const finishOnboarding = useFinishOnboarding(
    reportPapEvent,
    "FSS_BRIDGE_V1",
    true,
    false,
  );

  useDoOnceOnMount(() => {
    reportPapEvent(
      PAP_Shown_BridgeWelcomeModal({
        featureLine: "onboarding",
      }),
    );
  });

  const onCloseModal = React.useCallback(
    (isAddApps: boolean = false) => {
      reportPapEvent(
        PAP_Dismiss_BridgeWelcomeModal({
          featureLine: "onboarding",
          isAddAppsButton: isAddApps,
        }),
      );
      finishOnboarding();
      onClose?.(isAddApps);
    },
    [finishOnboarding, onClose, reportPapEvent],
  );

  const onAddApps = React.useCallback(() => {
    onCloseModal(true);
  }, [onCloseModal]);

  const onContinue = React.useCallback(() => {
    onCloseModal(false);
  }, [onCloseModal]);

  return (
    <InfoModal
      open={open}
      rootClassName={styles.welcomeModal}
      bodyClassName={styles.welcomeModalBody}
      width={1062}
      onClose={onContinue}
      closeOnOverlayClick={false}
      body={
        <SplitBox
          height="100%"
          leftContent={
            <Box
              className={styles.infoContentContainer}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box
                className={styles.infoContent}
                display="flex"
                flexDirection="column"
              >
                <Title weightVariant="emphasized" size="large">
                  {i18n.t("welcome_to_dash")}
                </Title>
                <Box display="flex" flexDirection="column" rowGap="Micro Large">
                  {FEATURES.map(([Icon, description]) => (
                    <Box
                      display="flex"
                      flexDirection="row"
                      key={description}
                      columnGap="Micro Small"
                    >
                      <UIIcon src={Icon} />
                      <Text size="large">{description}</Text>
                    </Box>
                  ))}
                </Box>
                <Box display="flex" flexDirection="row" columnGap="Micro Large">
                  {variant === "addApps" || variant === "override" ? (
                    <Button
                      className={styles.actionButton}
                      variant="primary"
                      size="large"
                      onClick={onContinue}
                    >
                      {i18n.t("continue_button")}
                    </Button>
                  ) : (
                    <>
                      <Button
                        className={styles.actionButton}
                        variant="opacity"
                        size="large"
                        onClick={onContinue}
                      >
                        {i18n.t("continue_button")}
                      </Button>
                      <Button
                        className={styles.actionButton}
                        variant="primary"
                        size="large"
                        onClick={onAddApps}
                      >
                        {i18n.t("add_apps")}
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          }
          rightContent={
            <Box
              className={styles.heroPane}
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              <video autoPlay muted loop>
                <source src={HERO_VIDEO} type="video/webm" />
              </video>
            </Box>
          }
        />
      }
    />
  );
}
